import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  ClipboardType,
  Trash2,
  Calendar,
  User,
  Activity,
  Loader2,
  Database,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import styles from '../../../styles/MasterView.module.css';
import api from '../../../api/axios';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import ProjectTypeDrawer from './ProjectTypeDrawer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewProjectType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projectType, setProjectType] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchProjectType = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/project-types/${id}`);
      if (response.data.success) {
        setProjectType(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching project type details:', error);
      toast.error('Failed to load project type details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectType();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Delete Project Type?',
      text: `Are you sure you want to delete ${projectType.type_name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/project-types/${projectType.id}`);
          if (response.data.success) {
            toast.success('Project type deleted successfully');
            navigate('/project-types');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting project type');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={styles['view-master-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Fetching Project Category...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!projectType) {
    return (
      <div className={styles['view-master-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['not-found-container']}>
            <h2>Project Type Not Found</h2>
            <button onClick={() => navigate('/project-types')} className={styles['btn-secondary']}>
              <ArrowLeft size={18} />
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles['view-master-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Project Type Details</h1>
              <p>View classification settings and related project statistics</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/project-types')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Update Category</span>
              </button>
              <button className={styles['btn-secondary']} onClick={handleDelete} style={{ color: '#ef4444' }}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          {/* Master Profile Header Card */}
          <div className={styles['master-profile-header']}>
            <div className={styles['header-main']}>
              <div 
                className={styles['avatar-large']}
                style={{ background: 'var(--primary-gradient)' }}
              >
                <ClipboardType size={32} />
              </div>
              <div className={styles['profile-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{projectType.type_name}</h1>
                  <span className={styles['status-badge']}>
                    <ShieldCheck size={14} /> ACTIVE CATEGORY
                  </span>
                </div>
                <div className={styles['master-meta']}>
                  <div className={styles['meta-item']}>
                    <Database size={16} />
                    <span>Classification: <strong>Project Type</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Registered: <strong>{new Date(projectType.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><Database size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Active Projects</label>
                <h2>12</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><CheckCircle2 size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Completed</label>
                <h2>48</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Growth Rate</label>
                <h2>+15%</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Hierarchy</label>
                <h2>Primary</h2>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Primary Details */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Database size={20} /></div>
                  <h3>Classification Config</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Category Descriptor</span>
                      <span className={styles['d-value']}>{projectType.type_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Type Identifier</span>
                      <span className={styles['d-value']}>PTYPE-0{projectType.id}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Related Projects */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={20} /></div>
                  <h3>Related Project Index</h3>
                </div>
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                  <p style={{ fontSize: '13px' }}>List of projects categorized under this type will be displayed here.</p>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Registry Info */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={20} /></div>
                  <h3>Registry Metadata</h3>
                </div>
                <div className={styles['info-block']}>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><User size={18} /></div>
                    <div className={styles['sidebar-text']}>
                       <label>REGISTERED BY</label>
                       <span>{projectType.creator?.name || 'System Admin'}</span>
                    </div>
                  </div>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><Calendar size={18} /></div>
                    <div className={styles['sidebar-text']}>
                       <label>CREATED ON</label>
                       <span>{new Date(projectType.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <ProjectTypeDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        projectType={projectType}
        onSave={fetchProjectType}
      />
    </div>
  );
};

export default ViewProjectType;
